import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Euro, CalendarCheck, TrendingUp, CalendarX } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { format, addWeeks, addMonths, addQuarters, addYears } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const PERIODS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Maand" },
  { key: "quarter", label: "Kwartaal" },
  { key: "year", label: "Jaar" },
];
const eur = (n) => `€ ${Number(n ?? 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Financial() {
  const [period, setPeriod] = useState("month");
  const [ref, setRef] = useState(new Date());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/financial", { params: { period, ref: format(ref, "yyyy-MM-dd") } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [period, ref]);
  useEffect(() => { load(); }, [load]);

  const shift = (dir) => {
    const fn = { week: addWeeks, month: addMonths, quarter: addQuarters, year: addYears }[period];
    setRef((d) => fn(d, dir));
  };
  const changePeriod = (p) => { setPeriod(p); setRef(new Date()); };

  const maxRev = Math.max(1, ...(data?.per_treatment || []).map((t) => t.revenue));

  return (
    <div>
      <h1 className="text-3xl font-medium tracking-tight mb-1">Financieel</h1>
      <p className="text-ink-muted mb-6">Inkomsten en boekingen per behandeling.</p>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Tabs value={period} onValueChange={changePeriod}>
          <TabsList className="bg-cream-subtle rounded-full">
            {PERIODS.map((p) => (
              <TabsTrigger key={p.key} value={p.key} data-testid={`period-${p.key}`} className="rounded-full">{p.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" className="rounded-full" onClick={() => shift(-1)} data-testid="period-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium min-w-[140px] text-center" data-testid="period-label">{data?.label ?? "–"}</span>
          <Button size="icon" variant="outline" className="rounded-full" onClick={() => shift(1)} data-testid="period-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Euro} label="Omzet" value={eur(data?.revenue_total)} testid="fin-revenue" accent />
        <StatCard icon={CalendarCheck} label="Boekingen" value={data?.bookings_total ?? "–"} testid="fin-bookings" />
        <StatCard icon={TrendingUp} label="Gem. per boeking" value={eur(data?.avg_per_booking)} testid="fin-avg" />
        <StatCard icon={CalendarX} label="Geannuleerd" value={`${data?.cancelled_count ?? 0} · ${eur(data?.cancelled_value)}`} testid="fin-cancelled" />
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mb-8" data-testid="revenue-chart">
        <h2 className="text-xl font-medium mb-5">Omzetverloop</h2>
        {!data?.series?.length ? (
          <p className="text-ink-muted text-sm">Geen gegevens in deze periode.</p>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.series} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F2EBE1" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#7A6E6E" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12, fill: "#7A6E6E" }} axisLine={false} tickLine={false} width={48}
                  tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  cursor={{ fill: "rgba(197,138,138,0.08)" }}
                  formatter={(v) => [eur(v), "Omzet"]}
                  contentStyle={{ borderRadius: 12, border: "1px solid #E8E2D9", fontSize: 13 }} />
                <Bar dataKey="revenue" fill="#C58A8A" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
        <h2 className="text-xl font-medium mb-5">Per behandeling</h2>
        {!data?.per_treatment?.length && <p className="text-ink-muted text-sm">Geen boekingen in deze periode.</p>}
        <div className="space-y-4">
          {(data?.per_treatment || []).map((t) => (
            <div key={t.treatment_id} data-testid={`fin-treatment-${t.treatment_id}`}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-medium">{t.treatment_name}</span>
                <span className="text-ink-muted">{t.count}× · <span className="text-ink font-medium">{eur(t.revenue)}</span></span>
              </div>
              <div className="h-2.5 rounded-full bg-cream-subtle overflow-hidden">
                <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${(t.revenue / maxRev) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, testid, accent }) => (
  <div data-testid={testid} className={`rounded-2xl border p-6 shadow-[0_8px_30px_rgb(74,63,63,0.04)] ${accent ? "bg-ink text-white border-ink" : "bg-white border-[#E8E2D9]"}`}>
    <Icon className={`w-5 h-5 mb-4 ${accent ? "text-white/80" : "text-brand"}`} strokeWidth={1.5} />
    <div className="text-2xl font-display">{value}</div>
    <div className={`text-sm mt-1 ${accent ? "text-white/70" : "text-ink-muted"}`}>{label}</div>
  </div>
);
